import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { fetchJobs, createApplication } from '@/lib/api';
import { PIPELINE_STAGES, STAGE_LABELS } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface AssignToJobDialogProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignToJobDialog({ candidateId, open, onOpenChange }: AssignToJobDialogProps) {
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedStage, setSelectedStage] = useState('sourced');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: jobsData, isLoading: isLoadingJobs } = useQuery({
    queryKey: ['jobs', { limit: 100 }],
    queryFn: () => fetchJobs({ limit: 100 }),
    enabled: open,
  });

  // Paused jobs are still actively worked (only unpublished from the site),
  // so candidates can be assigned to them alongside open jobs.
  const jobs = (jobsData?.data ?? []).filter(
    (job) => job.status === 'open' || job.status === 'paused',
  );

  const assignMutation = useMutation({
    mutationFn: () =>
      createApplication({
        jobId: selectedJobId,
        candidateId,
        stage: selectedStage,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidateApplications', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setSelectedJobId('');
      setSelectedStage('sourced');
      setError(null);
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to assign candidate to job');
    },
  });

  const handleSubmit = () => {
    if (!selectedJobId) {
      setError('Please select a job');
      return;
    }
    setError(null);
    assignMutation.mutate();
  };

  // Filter out terminal stages for initial assignment
  const assignableStages = PIPELINE_STAGES.filter(
    (s) => s !== 'hired' && s !== 'disqualified',
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to Job</DialogTitle>
          <DialogDescription>
            Select a job to assign this candidate to and choose the initial pipeline stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label>Job *</Label>
            {isLoadingJobs ? (
              <div className="flex items-center gap-2 text-sm text-text-tertiary">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading jobs...
              </div>
            ) : jobs.length === 0 ? (
              <p className="text-sm text-text-tertiary">No open or paused jobs available</p>
            ) : (
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}
                      {job.company ? ` - ${job.company.name}` : ''}
                      {job.status === 'paused' ? ' (Paused)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Initial Stage</Label>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableStages.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {STAGE_LABELS[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={assignMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedJobId || assignMutation.isPending}
          >
            {assignMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              'Add to Job'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
